import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";
import {
  STOCK_TAX_INTRADAY_REPORT,
  STOCK_TAX_REPORT_COLUMNS,
} from "../../../utils/equity/stockTaxReport";

export type FieldColumn = {
  key: string;
  value: string;
  locked?: boolean;
  minWidth?: number;
  align?: "left" | "center" | "right";
};

export const EQUITY_POINT_TO_POINT_REPORT = "Point to Point Return";

export const EQUITY_REPORT_TYPE_OPTIONS = [
  { value: "Holding", label: "Unrealized" },
  { value: "Realized", label: "Realized" },
  { value: "All", label: "All" },
  { value: "Transaction", label: "Transaction" },
  { value: "MIS Report", label: "MIS Report" },
  { value: STOCK_TAX_INTRADAY_REPORT, label: STOCK_TAX_INTRADAY_REPORT },
  { value: "Short Term Capital Gain", label: "Short Term Capital Gain" },
  { value: "Long Term Capital Gain", label: "Long Term Capital Gain" },
  { value: EQUITY_POINT_TO_POINT_REPORT, label: "Point to Point Return" },
] as const;

export const PRIVATE_EQUITY_REPORT_TYPE_OPTIONS = EQUITY_REPORT_TYPE_OPTIONS.filter(
  (opt) => opt.value !== "MIS Report" && opt.value !== STOCK_TAX_INTRADAY_REPORT,
);

export const EQUITY_QUICK_REPORTS = [
  { id: "holding", label: "Unrealized", reportType: "Holding" },
  { id: "realized", label: "Realized", reportType: "Realized" },
  { id: "all", label: "All", reportType: "All" },
  { id: "transaction", label: "Transaction", reportType: "Transaction" },
  { id: "intraday", label: STOCK_TAX_INTRADAY_REPORT, reportType: STOCK_TAX_INTRADAY_REPORT },
];

export const PRIVATE_EQUITY_QUICK_REPORTS = EQUITY_QUICK_REPORTS.filter(
  (q) => q.id !== "intraday",
);

export const INVESTOR_COLUMN: FieldColumn = {
  key: "investorDisplay",
  value: "Investor Name",
  locked: true,
  minWidth: 200,
};

/** aumsampaan STOCK Holding preset */
export const EQUITY_UNREALIZED_HOLDING_COLUMNS: FieldColumn[] = [
  { key: "symbol", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "units", value: "Qty", minWidth: 100 },
  { key: "averagePrice", value: "Average Price", minWidth: 112 },
  { key: "currentPrice", value: "Current Price", minWidth: 112 },
  { key: "amount", value: "Invested Amt.", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPerc", value: "P&L %", minWidth: 96 },
];

export const EQUITY_REALIZED_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "units", value: "Units Sold", minWidth: 100 },
  { key: "purchaseNAV", value: "Sell Price", minWidth: 112 },
  { key: "avgBuyPrice", value: "Avg Buy Price", minWidth: 112 },
  { key: "saleValue", value: "Sale Value", minWidth: 128 },
  { key: "realisedPnl", value: "Realized P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realized P&L%", minWidth: 120 },
];

export const EQUITY_TRANSACTION_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 },
  { key: "transType", value: "Type", locked: true, minWidth: 120 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "units", value: "Qty", minWidth: 100 },
  { key: "purchasePrice", value: "Price", minWidth: 112 },
  { key: "currentPrice", value: "Current Price", minWidth: 112 },
  { key: "amount", value: "Amount", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
  { key: "unrealisedPNL", value: "Unrealised P&L", minWidth: 132 },
  { key: "unrealisedPNLPercentage", value: "Unrealised P&L %", minWidth: 120 },
  { key: "realisedPnl", value: "Realised P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realised P&L %", minWidth: 120 },
];

export const EQUITY_ALL_COMMON_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "averageNav", value: "Average NAV", minWidth: 112 },
  { key: "currentNAV", value: "Current NAV", minWidth: 112 },
  { key: "costValue", value: "Invested Amount", minWidth: 128 },
  { key: "currentValue", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L %", minWidth: 96 },
];

export const EQUITY_MIS_COLUMNS: FieldColumn[] = [
  { key: "dateOfInvestment", value: "Date of Investment", locked: true, minWidth: 140 },
  { key: "symbol", value: "Stock Name", locked: true, minWidth: 200 },
  { key: "marketCap", value: "Market Cap", minWidth: 120 },
  { key: "investedAmount", value: "Purchase Value (₹)", minWidth: 140 },
  { key: "currentValue", value: "Current Value (₹)", minWidth: 140 },
  { key: "absoluteReturn", value: "AR (₹)", minWidth: 120 },
  { key: "absoluteReturnPerc", value: "AR (%)", minWidth: 96 },
];

export const EQUITY_P2P_COLUMNS: FieldColumn[] = [
  { key: "symbol", value: "Symbol", locked: true, minWidth: 112 },
  { key: "exchange", value: "Exchange", minWidth: 88 },
  { key: "transType", value: "Type", minWidth: 96 },
  { key: "units", value: "Units", minWidth: 112 },
  { key: "investedAmount", value: "Invested / Selling Amt.", minWidth: 152 },
  { key: "currentAmount", value: "Current Amount (₹)", minWidth: 152 },
  { key: "currentNAV", value: "Price", minWidth: 128 },
  { key: "avgPrice", value: "Avg Price", minWidth: 112 },
  { key: "xirr", value: "XIRR", minWidth: 96 },
  { key: "cagr", value: "CAGR", minWidth: 96 },
  { key: "unrealisedPNL", value: "Unrealised P&L", minWidth: 132 },
  { key: "unrealisedPNLPercentage", value: "Unrealised P&L%", minWidth: 120 },
  { key: "realisedPnl", value: "Realised P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realised P&L%", minWidth: 120 },
];

export const EQUITY_TAX_COLUMNS: FieldColumn[] = STOCK_TAX_REPORT_COLUMNS.map((c) => ({
  key: c.key,
  value: c.value,
  locked: c.locked,
}));

export const getEquityReportTypeOptions = (assetClass: string) =>
  assetClass === "PRIVATE_STOCK"
    ? PRIVATE_EQUITY_REPORT_TYPE_OPTIONS
    : EQUITY_REPORT_TYPE_OPTIONS;

export const getEquityQuickReports = (assetClass: string) =>
  assetClass === "PRIVATE_STOCK" ? PRIVATE_EQUITY_QUICK_REPORTS : EQUITY_QUICK_REPORTS;

export const getColumnsForEquityReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return EQUITY_UNREALIZED_HOLDING_COLUMNS;
    case "Realized":
      return EQUITY_REALIZED_COLUMNS;
    case "All":
      return EQUITY_ALL_COMMON_COLUMNS;
    case "Transaction":
      return EQUITY_TRANSACTION_COLUMNS;
    case "MIS Report":
      return EQUITY_MIS_COLUMNS;
    case EQUITY_POINT_TO_POINT_REPORT:
      return EQUITY_P2P_COLUMNS;
    case STOCK_TAX_INTRADAY_REPORT:
    case "Short Term Capital Gain":
    case "Long Term Capital Gain":
      return EQUITY_TAX_COLUMNS;
    default:
      return EQUITY_UNREALIZED_HOLDING_COLUMNS;
  }
};

export const reportTypeNeedsDateRange = (reportType: string): boolean =>
  [
    "Holding",
    EQUITY_POINT_TO_POINT_REPORT,
    STOCK_TAX_INTRADAY_REPORT,
    "Short Term Capital Gain",
    "Long Term Capital Gain",
  ].includes(reportType);

export const getEquityTableSegment = (assetClass: string): string =>
  assetClass === "PRIVATE_STOCK" ? "PRIVATE_STOCK" : "STOCK";
